import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { growthApi } from '../api/phase12Client';

export type EditTarget =
  | { kind: 'elasticity'; id: string; name: string; value: number | null; lower?: number | null; upper?: number | null; unit?: string | null; source?: string | null }
  | { kind: 'linkage'; id: string; name: string; value: number | null; unit?: string | null; basis?: string | null; reference?: string | null }
  | { kind: 'lever'; id: string; name: string };

interface EditorValue { open: (t: EditTarget, onSaved?: () => void) => void }
const Ctx = createContext<EditorValue | null>(null);

export function EditorProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<EditTarget | null>(null);
  const [onSaved, setOnSaved] = useState<(() => void) | undefined>();
  const [form, setForm] = useState<Record<string, string>>({});
  const [lever, setLever] = useState<any>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const open = useCallback((t: EditTarget, cb?: () => void) => {
    setTarget(t); setOnSaved(() => cb); setMsg(null); setLever(null);
    if (t.kind === 'elasticity') setForm({ central: String(t.value ?? ''), lower_bound: String(t.lower ?? ''), upper_bound: String(t.upper ?? ''), notes: '' });
    else if (t.kind === 'linkage') setForm({ elasticity: String(t.value ?? ''), linkage_basis: t.basis || 'analyst', reference: t.reference || '', rationale: '' });
    else { setForm({}); growthApi.lever(t.id).then((l) => { setLever(l); setForm({ qualitative_max_pp: String(l.qualitative_max_pp ?? ''), coefficient: String(l.coefficient ?? ''), weight: String(l.weight ?? ''), phase_in: String(l.phase_in ?? ''), note: '' }); }).catch((e) => setMsg(String(e))); }
  }, []);

  const close = () => setTarget(null);
  const num = (v: string) => (v === '' || v == null ? undefined : parseFloat(v));

  const save = async () => {
    if (!target) return;
    setBusy(true); setMsg(null);
    try {
      if (target.kind === 'elasticity') await growthApi.editElasticity(target.id, { central: num(form.central), lower_bound: num(form.lower_bound), upper_bound: num(form.upper_bound), notes: form.notes || undefined });
      else if (target.kind === 'linkage') await growthApi.editLinkage(target.id, { elasticity: num(form.elasticity), linkage_basis: form.linkage_basis || undefined, reference: form.reference || undefined, rationale: form.rationale || undefined, assumption_flag: form.linkage_basis === 'literature' || form.linkage_basis === 'accounting' ? false : undefined });
      else await growthApi.editLever(target.id, { qualitative_max_pp: num(form.qualitative_max_pp), coefficient: num(form.coefficient), weight: num(form.weight), phase_in: num(form.phase_in), note: form.note || undefined });
      onSaved?.(); close();
    } catch (e) { setMsg(String(e)); } finally { setBusy(false); }
  };

  const value = useMemo(() => ({ open }), [open]);
  const F = ({ k, label, type = 'number', step = '0.01' }: { k: string; label: string; type?: string; step?: string }) => (
    <div><label className="field-label">{label}</label><input className="field" type={type} step={step} value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
  );
  return (
    <Ctx.Provider value={value}>
      {children}
      {target && (
        <div className="modal-backdrop" onClick={close}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: 4 }}>{target.kind === 'elasticity' ? 'Edit elasticity' : target.kind === 'linkage' ? 'Edit linkage elasticity' : 'Edit lever parameters'}</h3>
            <div className="small" style={{ marginBottom: 10 }}>{target.name} <span className="mono">({target.id})</span>{'unit' in target && target.unit ? ` · ${target.unit}` : ''}{'source' in target && target.source ? <div>Source: {target.source}</div> : null}</div>
            {target.kind === 'elasticity' && <><F k="central" label="Central estimate" /><div className="grid-2"><F k="lower_bound" label="Lower bound" /><F k="upper_bound" label="Upper bound" /></div><F k="notes" label="Note (why this value)" type="text" /></>}
            {target.kind === 'linkage' && <><F k="elasticity" label="Elasticity" /><div><label className="field-label">Basis</label><select className="field" value={form.linkage_basis} onChange={(e) => setForm({ ...form, linkage_basis: e.target.value })}>{['literature', 'inherited', 'accounting', 'analyst'].map((b) => <option key={b}>{b}</option>)}</select></div><F k="reference" label="Reference" type="text" /><F k="rationale" label="Rationale" type="text" /></>}
            {target.kind === 'lever' && (lever ? <>
              <div className="small" style={{ marginBottom: 6 }}>{lever.policy_area} · method {lever.method || 'not GDP-linked'}</div>
              <div className="grid-2"><F k="qualitative_max_pp" label="Max GDP contribution G_max (pp at score 4)" step="0.005" /><F k="coefficient" label="Coefficient β (quantitative levers)" step="0.001" /></div>
              <div className="grid-2"><F k="weight" label="Weight w" /><F k="phase_in" label="Phase-in p (0–1)" /></div>
              <F k="note" label="Note (recorded in the audit log)" type="text" />
            </> : <div className="loading">Loading lever…</div>)}
            {msg && <div className="error" style={{ marginTop: 8 }}>{msg}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="btn-secondary" onClick={close}>Cancel</button>
              <button className="btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
            <div className="small" style={{ marginTop: 8 }}>Edits are saved to the model database and recorded in the analyst audit log; the dashboard recalculates immediately.</div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useEditor() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useEditor must be used within EditorProvider');
  return ctx;
}

/** Clickable value: opens the editor pop-up. */
export function Editable({ target, onSaved, children }: { target: EditTarget; onSaved?: () => void; children: ReactNode }) {
  const { open } = useEditor();
  return <button type="button" className="editable" title="Click to edit" onClick={() => open(target, onSaved)}>{children}<span className="editable-pen">✎</span></button>;
}
